import { useEffect, useState } from 'react'
import Layout from 'containers/Layout'
import moment from 'moment'
import _, { get } from 'lodash'
import apiGet from 'lib/network/apiGet'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'

import useModal from 'hooks/useModal'

import { IoPencil, IoSearch, IoTrashBin, IoAdd } from 'react-icons/io5'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'

import notify from 'components/atoms/Notify'
import apiDelete from 'lib/network/apiDelete'
import PageFooter from 'components/paginationComponent'
import { useSelector } from 'react-redux'

const Partners = () => {
  const history = useHistory()
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'

  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({})
  const [partners, setPartners] = useState([])
  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')

  const [selectedPartnerId, setSelectedPartnerId] = useState(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState({})

  const { id } = useParams()

  useEffect(() => {
    fetchPartners()
  }, [search, filter])

  const fetchPartners = async (page = 1) => {
    if (page === 1) {
      setLoading(true)
    }
    const params = {}
    params.pageNumber = page
    params.pageSize = 20
    params.query = search || undefined
    if (filter.sortBy && filter.sortOrder) {
      params.sortBy = filter.sortBy
      params.sortOrder = filter.sortOrder
    }

    const response = await apiGet(`partners`, params)
    setPartners(page === 1 ? response.data.partners : [...partners, ...response.data.partners])

    setPagination(response?.data?.pagination || {})
    setLoading(false)
  }

  // const handleAddPartnerSuccess = partnerId => {
  //   closeModal()
  //   fetchPartners()
  // }

  const deletePartner = id => {
    notify.deleteConfirm({
      title: t('partners.deletePartner'),
      message: t('partners.deleteConfirmMessage'),
      onConfirm: async () => {
        const response = await apiDelete(`partner/${id}`)
        if (response?.success) {
          fetchPartners()
          notify.success(t('partners.partnerDeletedSuccess'))
        }
      },
    })
  }

  const columns = [
    {
      name: t('partners.partnerName'),
      width: '25%',
      key: 'name',
      sortable: true,
      rendered: item => {
        return (
          <a href={`/partners/edit/${item._id}`} className={`flex items-center cursor-pointer no-underline ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex items-center cursor-pointer">
              <div className={isRTL ? 'mr-4' : 'ml-4'}>
                <div className="text-sm font-normal text-gray-900">{item.name || t('common.noData')}</div>
              </div>
            </div>
          </a>
        )
      },
    },
    {
      name: t('partners.partnerEmail'),
      width: '25%',
      key: 'email',
      sortable: true,
      rendered: item => {
        return (
          <div className={`flex items-center cursor-pointer ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={isRTL ? 'mr-4' : 'ml-4'}>
              <div>{item.email || t('common.noData')}</div>
            </div>
          </div>
        )
      },
    },
    {
      name: t('partners.phoneNumber'),
      width: '25%',
      key: 'phone',
      sortable: true,
      rendered: item => {
        return (
          <div className={`flex items-center cursor-pointer ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={isRTL ? 'mr-4' : 'ml-4'}>
              <div>{item.phone || t('common.noData')}</div>
            </div>
          </div>
        )
      },
    },
    {
      width: '20%',
      name: t('partners.website'),
      key: 'websiteUrl',
      sortable: true,
      rendered: item => {
        const websiteUrl = item.websiteUrl
        return (
          <div>
            {websiteUrl ? (
              <a
                // eslint-disable-next-line
                href={websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                {websiteUrl}
              </a>
            ) : (
              <span className="text-gray-400 text-sm">{t('partners.noWebsite')}</span>
            )}
          </div>
        )
      },
    },
    {
      width: '15%',
      name: t('partners.created'),
      key: 'createdAt',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        const createdAt = createdTime ? moment(createdTime).fromNow() : 'N/A'
        return <span className="text-sm text-gray-500">{createdAt}</span>
      },
    },
    {
      width: '15%',
      name: t('partners.updated'),
      key: 'updatedAt',
      sortable: true,
      rendered: item => {
        const updatedTime = get(item, 'updatedAt', '')
        const updatedAt = updatedTime ? moment(updatedTime).fromNow() : 'N/A'
        return <span className="text-sm text-gray-500">{updatedAt}</span>
      },
    },
  ]

  return (
    <Layout
      showLogo={!!id}
      breadcrumbs={[{ name: t('partners.breadcrumb'), pathname: '/partners' }]}
      headerRight={
        <div className={`flex items-center justify-end ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="mt-1 relative rounded-md shadow-sm flex-grow">
            <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
            <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
              <IoSearch className={`${isRTL ? 'ml-3' : 'mr-3'} h-4 w-4 text-gray-400`} aria-hidden="true" />
            </div>
            <input
              onChange={e => setSearch(e.target.value)}
              value={search}
              type="text"
              name="search"
              id="search"
              className={`block ${isRTL ? 'pr-9' : 'pl-9'} shadow-none sm:text-sm bg-gray-100 border-0 focus:outline-none rounded-sm`}
              placeholder={t('partners.searchPlaceholder')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="mt-1 relative rounded-md shadow-sm ml-4">
            <Button onClick={() => history.push('/partners/add')}>{t('partners.addPartner')}</Button>
          </div>
        </div>
      }
      title={t('partners.title')}
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {!loading ? (
                  <>
                    {partners?.length === 0 ? (
                      <EmptyState v2 description={t('partners.noPartnersFound')} />
                    ) : (
                      <Table
                        hasMore={pagination?.currentPage !== pagination?.totalPages}
                        fetchData={() => fetchPartners(pagination?.currentPage + 1)}
                        sortBy={filter.sortBy}
                        sortOrder={filter.sortOrder}
                        handleSort={(sortBy, sortOrder) => {
                          setFilter(prevFilter => ({ ...prevFilter, sortBy, sortOrder }))
                        }}
                        data={partners}
                        columns={columns}
                      />
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  )
}

export default Partners
